import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authService, communityService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import VatucoLogo from './VatucoLogo';

const MAIN_NAV_ITEMS = [
  { path: '/',             icon: 'home',        label: 'Home' },
  { path: '/communities',  icon: 'group_work',  label: 'Communities' },
  { path: '/messages',     icon: 'forum',       label: 'Messages', requiresChat: true },
  { path: '/reservations', icon: 'swap_horiz',  label: 'Mis compraventas' },
  { path: '/wishlist',     icon: 'bookmark',    label: 'Saved' },
];

const BOTTOM_NAV_ITEMS = [
  { path: '/',             icon: 'home',         label: 'Home' },
  { path: '/communities',  icon: 'group_work',   label: 'Communities' },
  { path: '/messages',     icon: 'forum',        label: 'Messages', requiresChat: true },
  { path: '/create-product', icon: 'add_circle', label: 'Sell', isCta: true },
  { path: '/reservations', icon: 'swap_horiz',   label: 'Compraventas' },
  { path: '/album',        icon: 'emoji_events', label: 'Album' },
  { path: '/profile',      icon: 'person',       label: 'Profile' },
];

const COLLAPSE_KEY = 'cm_sidebar_collapsed';

export default function DashboardSidebar({ activePath }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { count: unreadCount, available: chatAvailable } = useUnreadMessages();
  const active = activePath || location.pathname;
  const visibleNavItems = MAIN_NAV_ITEMS.filter((item) => !item.requiresChat || chatAvailable);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_KEY);
      if (saved !== null) return saved === 'true';
    } catch {}
    return typeof window !== 'undefined' && window.innerWidth < 1280;
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, String(collapsed)); } catch {}
  }, [collapsed]);

  useEffect(() => {
    let wasNarrow = window.innerWidth < 1280;
    const onResize = () => {
      const isNarrow = window.innerWidth < 1280;
      if (isNarrow !== wasNarrow) {
        wasNarrow = isNarrow;
        if (isNarrow) setCollapsed(true);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { data: userProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => { const r = await authService.getCurrentUser(); return r.data; },
    enabled: !!user,
  });

  const { data: communities = [] } = useQuery({
    queryKey: ['userCommunities'],
    queryFn: async () => { const r = await communityService.getUserCommunities(); return r.data; },
    enabled: !!user,
  });

  const userName = userProfile?.name || user?.name || 'User';
  const userInitial = userName.charAt(0).toUpperCase();
  const visibleCommunities = communities.slice(0, 5);
  const hiddenCount = communities.length - 5;

  const navItemClass = (path) => {
    const isActive = path === active;
    const base = `flex items-center rounded-lg transition-colors duration-150 cursor-pointer relative`;
    const layout = collapsed ? 'justify-center py-2.5 px-0 w-10 mx-auto' : 'gap-3 py-2.5 px-3';
    const state = isActive
      ? 'bg-[var(--chrome-active-bg)] text-[var(--chrome-strong)] font-semibold border-l-2 border-[var(--chrome-active-border)]'
      : 'text-[var(--chrome-muted)] hover:bg-[var(--chrome-hover-bg)] hover:text-[var(--chrome-strong)] border-l-2 border-transparent';
    return `${base} ${layout} ${state}`;
  };

  return (
    <>
      {/* ── DESKTOP LEFT SIDEBAR ── */}
      <aside
        className={`hidden mobile-md:flex h-full flex-col py-5 shrink-0 z-50 overflow-y-auto overflow-x-hidden transition-[width] duration-300 ease-in-out ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ backgroundColor: 'var(--chrome-sidebar-bg)', borderRight: '1px solid var(--chrome-border-subtle)' }}
      >
        {/* Wordmark */}
        <div className={`mb-4 ${collapsed ? 'px-2 flex justify-center' : 'px-5'}`}>
          <a
            onClick={() => navigate('/')}
            className="cursor-pointer select-none"
            title={collapsed ? 'Vatuco' : undefined}
          >
            {collapsed
              ? <span className="text-lg font-extrabold text-primary">V</span>
              : <VatucoLogo className="text-xl" primary="var(--chrome-strong)" />
            }
          </a>
        </div>

        {/* Nav items */}
        <nav className={`flex flex-col gap-0.5 ${collapsed ? 'px-2' : 'px-3'}`}>
          {visibleNavItems.map(({ path, icon, label }) => {
            const tLabel = t(label);
            const showBadge = path === '/messages' && unreadCount > 0;
            const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);
            return (
              <a
                key={path}
                onClick={() => navigate(path)}
                className={navItemClass(path)}
                title={collapsed ? tLabel : undefined}
              >
                <span className="material-symbols-outlined text-[24px] shrink-0 relative">
                  {icon}
                  {showBadge && collapsed && (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                      {badgeText}
                    </span>
                  )}
                </span>
                {!collapsed && <span className="text-[15px] flex-1">{tLabel}</span>}
                {!collapsed && showBadge && (
                  <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {badgeText}
                  </span>
                )}
              </a>
            );
          })}
        </nav>

        {/* ── Album button (single, dorado animado) ───────────────────── */}
        <div className={`mt-2 ${collapsed ? 'px-2' : 'px-3'}`}>
          <a
            onClick={() => navigate('/album')}
            className={`golden-button group flex items-center rounded-lg cursor-pointer transition-transform hover:scale-[1.015] ${
              collapsed ? 'justify-center py-2 px-0 w-10 mx-auto' : 'gap-3 py-2.5 px-3'
            } ${active === '/album' ? 'ring-2 ring-amber-400/60' : ''}`}
            title={collapsed ? t('Album') : undefined}
          >
            <span className="material-symbols-outlined text-[24px] shrink-0 text-amber-900 drop-shadow-sm" style={{ fontVariationSettings: "'FILL' 1" }}>emoji_events</span>
            {!collapsed && (
              <span className="text-[15px] font-bold text-amber-950 drop-shadow-sm">
                {t('Album')}
              </span>
            )}
          </a>
        </div>

        {/* Communities section — hidden on tablet-lg+ where the right sidebar shows them */}
        {!collapsed && communities.length > 0 && (
          <div className="tablet-lg:hidden mt-5 px-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--chrome-faint)] px-3 mb-1.5">
              {t('Communities')}
            </p>
            <div className="flex flex-col gap-0.5">
              {visibleCommunities.map((c) => (
                <a
                  key={c.id}
                  onClick={() => navigate(`/communities/${c.id}`)}
                  className="flex items-center gap-2.5 py-2 px-3 rounded-lg cursor-pointer text-[var(--chrome-muted)] hover:bg-[var(--chrome-hover-bg)] hover:text-[var(--chrome-strong)] transition-colors"
                >
                  <span className="material-symbols-outlined text-[16px] shrink-0">group_work</span>
                  <span className="text-[15px] truncate">{c.name}</span>
                </a>
              ))}
              {hiddenCount > 0 && (
                <a
                  onClick={() => navigate('/communities')}
                  className="flex items-center gap-2.5 py-2 px-3 rounded-lg cursor-pointer text-[var(--chrome-faint)] hover:text-[var(--chrome-muted)] text-xs transition-colors"
                >
                  +{t('{{count}} more', { count: hiddenCount })}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Sell CTA + user + collapse toggle */}
        <div className={`mt-auto pt-4 ${collapsed ? 'px-2' : 'px-3'}`} style={{ borderTop: '1px solid var(--chrome-border-divider)' }}>
          <a
            onClick={() => navigate('/create-product')}
            className={`flex items-center justify-center gap-2 rounded-lg bg-primary text-on-primary font-semibold transition-opacity hover:opacity-90 cursor-pointer ${collapsed ? 'py-2.5 w-10 mx-auto' : 'py-2.5 px-4 w-full'}`}
            title={collapsed ? t('Sell Now') : undefined}
          >
            <span className="material-symbols-outlined text-[18px] shrink-0">add_circle</span>
            {!collapsed && <span className="text-[15px]">{t('Sell Now')}</span>}
          </a>

          <a
            onClick={() => navigate('/profile')}
            className={`flex items-center mt-3 rounded-lg cursor-pointer transition-colors hover:bg-[var(--chrome-hover-bg)] ${collapsed ? 'justify-center py-2 px-0 w-10 mx-auto' : 'gap-3 p-2'}`}
            title={collapsed ? userName : undefined}
          >
            {(() => {
              // Reusamos el query ['userProfile'] (ya cacheado por ProfileNew):
              // preferimos profilePictureId, caemos a profilePictureUrl si el
              // id no llegó. Esto NO suma calls — sale del mismo query.
              const pic = userProfile?.profilePictureId
                ? getImageUrl(userProfile.profilePictureId)
                : userProfile?.profilePictureUrl || null;
              return pic
                ? <img alt="" className="w-8 h-8 rounded-full object-cover shrink-0" src={pic} />
                : <div className="w-8 h-8 rounded-full text-white flex items-center justify-center font-bold text-sm shrink-0" style={{ backgroundColor: 'var(--chrome-avatar)' }}>{userInitial}</div>;
            })()}
            {!collapsed && (
              <div className="overflow-hidden">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--chrome-strong)' }}>{userName}</p>
                <p className="text-[10px] truncate" style={{ color: 'var(--chrome-faint)' }}>{t('View profile')}</p>
              </div>
            )}
          </a>

          <a
            onClick={() => setCollapsed(c => !c)}
            className={`flex items-center justify-center mt-2 py-1.5 rounded-lg cursor-pointer transition-colors hover:bg-[var(--chrome-hover-bg)] ${collapsed ? 'w-10 mx-auto' : 'w-full'}`}
            style={{ color: 'var(--chrome-faint)' }}
            title={collapsed ? t('Expandir barra lateral') : t('Colapsar barra lateral')}
          >
            <span className="material-symbols-outlined text-[18px]">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </a>
        </div>
      </aside>

      {/* ── MOBILE BOTTOM TAB BAR ── */}
      <nav
        className="mobile-md:hidden fixed bottom-0 left-0 right-0 z-40 flex items-center justify-between px-1.5 border-t"
        style={{
          backgroundColor: 'var(--chrome-sidebar-bg)',
          borderColor: 'var(--chrome-border-subtle)',
          paddingBottom: 'calc(0.5rem + var(--safe-area-bottom))',
          paddingTop: '0.5rem',
        }}
      >
        {BOTTOM_NAV_ITEMS.filter((i) => !i.requiresChat || chatAvailable).map(({ path, icon, label, isCta }) => {
          const isActive = path === active;
          if (isCta) {
            return (
              <a
                key={path}
                onClick={() => navigate(path)}
                className="flex flex-col items-center gap-0.5 cursor-pointer flex-1 min-w-0"
              >
                <div className="w-9 h-9 rounded-full bg-primary text-on-primary flex items-center justify-center shadow-sm">
                  <span className="material-symbols-outlined text-[20px]">add</span>
                </div>
              </a>
            );
          }
          const showBadge = path === '/messages' && unreadCount > 0;
          const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);
          return (
            <a
              key={path}
              onClick={() => navigate(path)}
              className="flex flex-col items-center gap-0.5 cursor-pointer flex-1 min-w-0 px-0.5"
              style={{ color: isActive ? 'var(--chrome-active-border)' : 'var(--chrome-muted)' }}
            >
              <span className="material-symbols-outlined text-[22px] relative" style={{ fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0" }}>
                {icon}
                {showBadge && (
                  <span className="absolute -top-1 -right-2 min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {badgeText}
                  </span>
                )}
              </span>
              <span className="text-[9px] font-medium truncate max-w-full">{t(label)}</span>
            </a>
          );
        })}
      </nav>
    </>
  );
}
